/*
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */

import '../../common/beam/BeamMuiStyleOverrides.css'
import React, {useCallback, useEffect, useState} from 'react';
import appTheme from '../../common/theme/appTheme.json';
import {Box, Button} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {GridActionsCellItem, GridColDef} from '@mui/x-data-grid';
import CustomDataGrid from '../../common/data_grid/CustomDataGrid';
import ModalDialog from '../../common/dialogs/ModalDialog';
import {Link, useHistory, useRouteMatch} from 'react-router-dom';
import ErrorMessage from '../../common/layout/ErrorMessage';
import {StyledProvisioningPageHeading} from '../../provisioning/StyledProvisioningPageHeading';
import ModalLoadingSpinner from '../../common/layout/ModalLoadingSpinner';

import {ajaxRaw} from '../../../utils/StoreUtils';
import {HnaRequest, HnaRequestStatus} from "../../../shared/entities/hnaRequest";
import {toTitleCase} from "../../../utils/StringUtils";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {shallowEqual, useDispatch, useSelector} from "react-redux";
import {selectUserData} from "../../../store";
import {areAllowedRolesPresent} from "../../../utils/RBACUtils";
import {Authorities} from "../../../shared/entities/user";
import BeamTooltip from "../../common/beam/BeamTooltip";
import {RenderTooltipCell} from "../../common/data_grid/TooltipCell";
import {setSelectedMissionPlanId} from "../../../store/missionPlans/actions";
import {formatDottedDateYYYYMMDD} from "../../../utils/DateTimeUtils";

interface HnaRequestsTableProps {
  // Required for displaying tables in unit tests
  disableVirtualization?: boolean;
}

const HnaRequestsTable = ({ disableVirtualization = false }: HnaRequestsTableProps) => {
  const { path } = useRouteMatch();
  const [isLoadingData, setIsLoadingData] = useState<boolean>(false);
  const [dataArray, setDataArray] = useState<HnaRequest[]>([]);
  const [error, setError] = useState<string>('');
  const [deleteModalTarget, setDeleteModalTarget] = useState<HnaRequest | undefined>(undefined);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const userData = useSelector(selectUserData, shallowEqual);
  const history = useHistory();
  const dispatch = useDispatch();

  const loadTable = () => {
    setIsLoadingData(true);
    ajaxRaw.get(`/mpt/hna-requests`).subscribe({
      next: value => {
        setIsLoadingData(false);
        setDataArray(value.response);
      },
      error: err => {
        setIsLoadingData(false);
        setError(err.message);
      },
    });
  };
  useEffect(loadTable, []);

  // If the user is not an HNA approver, and the request state is not 'REQUESTED', then the user should not be able to edit the request.
  const canEditRequest = (request: HnaRequest) => {
    return (
      areAllowedRolesPresent(userData, Authorities.ROLE_HNA_APPROVER) || request.status === HnaRequestStatus.REQUESTED
    );
  };

  // Wrapper to disable action buttons if a user cannot edit a request. Also adds a helpful tooltip.
  const actionButtonWrapper = (request: HnaRequest, action: string, icon: JSX.Element) => {
    const statusTranslations = {
      [HnaRequestStatus.PROCESSING]: 'Pending',
      [HnaRequestStatus.COMPLETE]: 'Completed',
      [HnaRequestStatus.EXPIRED]: 'Expired',
    };

    return canEditRequest(request) ? (
      icon
    ) : (
      <BeamTooltip title={`${statusTranslations[request.status]} requests cannot be ${action}`} placement={'left'}>
        {icon}
      </BeamTooltip>
    );
  };

  // If the user is an HNA approver, then the view action should be 'review' instead of 'view'.
  // From either view, the user then has the ability to navigate to the /edit route.
  let viewActionLink = `${path}/view`;
  if (areAllowedRolesPresent(userData, Authorities.ROLE_HNA_APPROVER)) {
    viewActionLink = `${path}/review`;
  }

  const columns: GridColDef[] = [
    {
      field: 'missionNumber',
      headerName: 'Mission Number',
      flex: 1,
      renderCell: params => {
        // If missionPlanId is present, then render a link to the mission plan
        const missionPlanId = params.row.missionPlanId;
        if (missionPlanId) {
          return (
            <span
              onClick={() => {
                dispatch(setSelectedMissionPlanId(missionPlanId));
                history.push(`/plans/performance-outlook`);
              }}
              style={{
                textDecorationLine: 'underline',
                cursor: 'pointer',
              }}
            >
              {params.value}
            </span>
          );
        } else {
          return params.value;
        }
      },
    },
    {
      field: 'tailNumbers',
      headerName: 'Tail Number(s)',
      flex: 1,
      renderCell: RenderTooltipCell('right'),
      valueGetter: params => {
        return (params.value as string[]).join(', ');
      },
    },
    // APACS ID only visible to HNA approvers
    ...(areAllowedRolesPresent(userData, Authorities.ROLE_HNA_APPROVER)
      ? [
          {
            field: 'apacsId',
            headerName: 'APACS ID',
            flex: 1,
          },
        ]
      : []),
    {
      field: 'missionStartDate',
      headerName: 'Mission Start',
      flex: 1,
      valueGetter: params => {
        return formatDottedDateYYYYMMDD(params.value);
      },
    },
    {
      field: 'requestorOrgName',
      headerName: 'Organization',
      flex: 1,
    },
    {
      field: 'requestedAt',
      headerName: 'Date Requested',
      flex: 1,
      valueGetter: params => {
        return formatDottedDateYYYYMMDD(params.value);
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      valueGetter: params => {
        return toTitleCase(params.value as string);
      },
    },
    {
      field: 'authorizationStartDate',
      headerName: 'Authorization Dates',
      flex: 1,
      valueGetter: params => {
        const startDate = params.row.authorizationStartDate;
        const endDate = params.row.authorizationEndDate;
        return startDate && endDate ? `${formatDottedDateYYYYMMDD(startDate)} - ${formatDottedDateYYYYMMDD(endDate)}` : '- -';
      },
    },
    {
      field: 'actions',
      headerName: '',
      type: 'actions',
      flex: 1,
      align: 'right',
      cellClassName: 'ActionsCell-TopAligned',
      getActions: params => [
        // UP-5803: Implement this
        // <GridActionsCellItem
        //   key={'duplicate-object'}
        //   icon={<FileCopy />}
        // />,
        <GridActionsCellItem
          key={'delete-object'}
          label={'delete-object'}
          icon={actionButtonWrapper(params.row, 'deleted', <DeleteIcon />)}
          onClick={() => {
            setDeleteModalTarget(params.row);
            setShowDeleteModal(true);
          }}
          disabled={!canEditRequest(params.row)}
        />,
        <GridActionsCellItem
          key={'view-object'}
          label={'view-object'}
          icon={<ArrowForwardIcon />}
          component={Link}
          to={`${viewActionLink}/${params.row.id}`}
        />,
      ],
    },
  ];

  const CreateObjectButton = useCallback(
    () => (
      <Button
        component={Link}
        to={`${path}/new`}
        startIcon={<AddIcon />}
        variant="contained"
        sx={{
          marginLeft: 'auto',
          color: '#000000',
          letterSpacing: '0.8px',
          backgroundColor: appTheme.mptLightBlue,
          '&:hover': {
            backgroundColor: appTheme.mptLightBlue,
            svg: {
              color: '#000000',
            },
          },
        }}
      >
        Create new HNA Request
      </Button>
    ),
    [path],
  );

  return (
    <>
      {error ? <ErrorMessage>{error}</ErrorMessage> : null}

      <StyledProvisioningPageHeading>
        HNA Requests {dataArray ? `(${dataArray.length})` : ''}
      </StyledProvisioningPageHeading>

      {isLoadingData ? <ModalLoadingSpinner /> : null}

      <Box display="flex" justifyContent="space-between" flexDirection={'column'} height={'100%'}>
        <CustomDataGrid
          rows={dataArray || []}
          getRowHeight={() => 'auto'}
          getEstimatedRowHeight={() => 49}
          columns={columns}
          initialState={{
            sorting: {
              sortModel: [{ field: 'requestedAt', sort: 'desc' }],
            },
          }}
          toolbarActionButtons={[<CreateObjectButton key={'add-object-button'} />]}
          slotProps={{
            toolbar: {
              csvOptions: {
                fileName: 'HNA-Requests',
              },
            },
          }}
          disableVirtualization={disableVirtualization}
        />
      </Box>

      <ModalDialog
        open={showDeleteModal}
        onLeftBtnClicked={() => {
          setShowDeleteModal(false);
        }}
        onRightBtnClicked={() => {
          ajaxRaw.delete(`/mpt/hna-requests/${deleteModalTarget!.id}`).subscribe({
            next: value => {
              loadTable();
              setShowDeleteModal(false);
            },
            error: err => {
              loadTable();
              setError(err.message);
              setShowDeleteModal(false);
            },
          });
        }}
        onExited={() => {
          // Remove the delete target after the dialog's exit animation has finished. Otherwise, the `message` below
          // will briefly display `undefined` for the organization's display name
          setDeleteModalTarget(undefined);
        }}
        title={
          <Box display={'flex'} gap={'8px'} alignItems={'center'}>
            Confirm Deletion
          </Box>
        }
        message={
          <>
            Are you sure you want to delete this HNA Request?
            <br />
            This action cannot be undone.
          </>
        }
      />
    </>
  );
};

export default HnaRequestsTable;
